<template>
	<div>
		<v-snackbar v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
			{{ snackbar.message }}
			<v-btn :color="snackbar.buttonColor" flat @click="snackbar.show = false"> Close </v-btn>
		</v-snackbar>

		<v-card flat v-if="positionTemplate">
			<v-toolbar card prominent>
				<div class="text-xs-left">
					<h4 class="headline">
						{{ positionTemplate.title }}
					</h4>
				</div>

				<v-spacer></v-spacer>

				<v-chip v-if="positionTemplate.isArchived" color="warning" dark small>Archived</v-chip>

				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<v-btn icon v-on="on" @click="showSendEmailDialog = true"><v-icon dark>mail</v-icon></v-btn>
					</template>
					<span class="noPrint">Email Position Template</span>
				</v-tooltip>

				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<v-btn icon v-on="on" @click="printClick">
							<v-icon>print</v-icon>
						</v-btn>
					</template>
					<span class="noPrint">Print Position Template</span>
				</v-tooltip>

				<v-tooltip bottom v-if="canEdit">
					<template v-slot:activator="{ on }">
						<v-btn v-on="on" icon @click="onEditClick"><v-icon dark>edit</v-icon> </v-btn>
					</template>
					<span class="noPrint">Edit Position Template</span>
				</v-tooltip>

				<v-menu v-if="canEdit">
					<template #activator="{ on: menu }">
						<v-tooltip bottom>
							<template #activator="{ on: tooltip }">
								<v-icon v-on="{ ...tooltip, ...menu }"> more_vert </v-icon>
							</template>
							<span>More Options</span>
						</v-tooltip>
					</template>
					<v-list>
						<v-list-tile small @click="showConfirmDeleteDialog = true">
							<v-icon class="mr-1">delete</v-icon> <v-list-tile-title>Delete</v-list-tile-title>
						</v-list-tile>
						<v-list-tile small @click="onDuplicateClick">
							<v-list-tile-title><v-icon class="mr-1">content_copy</v-icon> Create a Duplicate</v-list-tile-title>
						</v-list-tile>
					</v-list>
				</v-menu>
			</v-toolbar>
			<v-card-text>
				<div class="header printOnly">
					<v-layout row wrap>
						<v-flex md3 class="text-xs-left">
							<div class="logo">
								<img :src="loggedInTenant.logoUrl" />
							</div>
						</v-flex>

						<v-flex md6 class="mt-3" style="text-align: center">
							<div style="margin-bottom: 5px" class="info-text">Position Description</div>
							<h1>{{ positionTemplate.title }}</h1>
						</v-flex>

						<v-flex md3 class="text-xs-left mt-3">
							<div class="header-right info-text">
								<div>Generated by Your HR System</div>
								<div>{{ new Date() | formatDate }}</div>
							</div>
						</v-flex>
					</v-layout>
				</div>

				<div class="content">
					<v-layout row wrap>
						<v-flex text-xs-left md8 sm12>
							<div class="text-xs-left">
								<h2 style="margin-bottom: 20px">Position Purpose</h2>
							</div>

							<p style="font-size: 13px; margin-bottom: 20px">{{ positionTemplate.description }}</p>
						</v-flex>
					</v-layout>

					<v-divider style="margin-bottom: 20px"></v-divider>

					<div v-for="(type, index) in pdItemsSorted" :key="index">
						<div class="text-xs-left">
							<h2 style="margin-top: 20px; margin-bottom: 10px">
								{{ type.typeFriendly }}
							</h2>
						</div>

						<div v-for="t in type.items" :key="t.id">
							<div class="text-xs-left">
								<h4 v-if="t.category !== 'N/A'" style="margin-top: 30px; margin-bottom: 20px; color: rgba(0, 0, 0, 0.54)">
									{{ t.category }}
								</h4>
							</div>
							<div v-for="s in t.items" :key="s.id" class="item">
								<v-layout row wrap style="margin-bottom: 5px">
									<v-flex text-xs-left md8 sm12>
										<div style="font-size: 13px">
											<div style="border-left-color: lightgray; padding-left: 10px; padding-right: 10px" :class="{ 'item-text': true }">
												<table>
													<tr>
														<td>
															<v-btn :class="{ 'item-btn': true, disabled: true }" fab small>
																<v-icon style="font-size: 14px">keyboard_arrow_right</v-icon>
															</v-btn>
														</td>
														<td>{{ s.item }}</td>
													</tr>
												</table>
											</div>

											<div v-for="outcome in s.outcomes" :key="outcome.id" style="margin-left: 20px">
												<div style="border-left-color: lightgray; padding-left: 10px; padding-right: 10px" :class="{ 'item-text': true }">
													<table>
														<tr>
															<td>
																<v-btn :class="{ 'item-btn': true, disabled: true }" fab small>
																	<v-icon style="font-size: 14px">keyboard_arrow_right</v-icon>
																</v-btn>
															</td>
															<td>{{ outcome.outcome }}</td>
														</tr>
													</table>
												</div>
											</div>
										</div>
									</v-flex>
								</v-layout>
							</div>
						</div>

						<v-divider style="margin-bottom: 20px"></v-divider>
					</div>
				</div>
			</v-card-text>
		</v-card>

		<confirmation-dialog
			v-if="showConfirmDeleteDialog"
			:dialog="showConfirmDeleteDialog"
			text="Are you sure you want to delete this position template?"
			:isConfirming="isDeleting"
			@onConfirm="onDelete"
			@onCancel="showConfirmDeleteDialog = false"
		/>

		<send-email-dialog
			v-if="showSendEmailDialog"
			:dialog="showSendEmailDialog"
			title="Email Position Template"
			:subject="$_getEmailMeta(loggedInEmployee, positionTemplate).subject"
			:body="$_getEmailMeta(loggedInEmployee, positionTemplate).body"
			@onCancel="showSendEmailDialog = false"
			@onSent="onSent"
		/>

		<loading-modal v-if="isLoading" loading-text="Loading Position Template..." />

		<v-dialog v-if="duplicateDialog" v-model="duplicateDialog" persistent max-width="500px">
			<v-card>
				<v-form>
					<v-toolbar flat dense cad>
						<v-toolbar-title class="subheading">Create a Duplicate</v-toolbar-title>
						<v-spacer></v-spacer>
					</v-toolbar>

					<v-divider></v-divider>
					<v-card-text>
						<v-text-field v-model="duplicateForm.title" ref="duplicateFormTitle" :error-messages="duplicateFormTitleErrors" label="Position Title" required @input="$v.duplicateForm.title.$touch()" @blur="$v.duplicateForm.title.$touch()"> </v-text-field>
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn flat @click="duplicateDialog = false">Cancel</v-btn>
						<v-btn color="primary" flat :disabled="isDuplicating" :loading="isDuplicating" @click="duplicatePositionDescription">Duplicate <span slot="loader">Saving...</span></v-btn>
					</v-card-actions>
				</v-form>
			</v-card>
		</v-dialog>

		<v-footer color app height="55" class="noPrint" v-if="loggedInEmployee.isAdmin">
			<v-spacer></v-spacer>
			<footer-back-btn @click="backBtnClicked" />
		</v-footer>

	</div>
</template>

<script>
	import SendEmailDialog from '@/components/email/SendEmailDialog';
	import LoadingModal from '@/components/LoadingModal';
	import typeFriendlyMixin from '@/mixins/typeFriendlyMixin';
	import emailMixin from '@/mixins/emailMixin';
	import positionTemplateEmailMixin from '@/mixins/positionTemplateEmailMixin';
	import { validationMixin } from 'vuelidate';
	import { required } from 'vuelidate/lib/validators';
	import { mapGetters } from 'vuex';

	export default {
		components: {
			SendEmailDialog,
			LoadingModal
		},
		props: {
			id: {
				required: true,
				type: String
			}
		},
		mixins: [typeFriendlyMixin, emailMixin, positionTemplateEmailMixin, validationMixin],
		data() {
			return {
				snackbar: {
					show: false,
					message: '',
					color: undefined,
					buttonColor: 'pink',
					timeout: 6000
				},
				positionTemplate: null,
				isLoading: false,
				showSendEmailDialog: false,
				showConfirmDeleteDialog: false,
				isDeleting: false,
				duplicateForm: {
					title: ''
				},
				duplicateDialog: false,
				isDuplicating: false
			};
		},
		created() {
			this.$store.dispatch('setPageTitle', 'Position Template');

			this.isLoading = true;
			this.$store.dispatch('positionTemplates/getById', this.id).then(
				(positionTemplate) => {
					this.isLoading = false;
					this.positionTemplate = positionTemplate;
				},
				(error) => {
					this.isLoading = false;
					this.showSnackbar('An error occurred loading position template', true);
				}
			);
		},
		validations: {
			duplicateForm: {
				title: { required }
			}
		},
		computed: {
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee',
				loggedInTenant: 'loggedInTenant'
			}),
			pdItemsSorted() {
				// group by type
				let groupedByType = _.groupBy(this.positionTemplate.positionItems, 'type');
				let result = [];

				for (let type in groupedByType) {
					result.push({
						type: type,
						typeFriendly: this.getTypeFriendly(type, true),
						items: []
					});

					// group by category
					let groupedByCategory = _.groupBy(groupedByType[type], 'category');

					for (let category in groupedByCategory) {
						result[result.length - 1].items.push({
							category: category,
							items: groupedByCategory[category]
						});
					}
				}

				let sortOrder = [1, 2, 5, 0, 3, 4];
				let resultsOrdered = [];
				for (let i = 0; i < sortOrder.length; i++) {
					for (let x = 0; x < result.length; x++) {
						if (result[x].type == sortOrder[i]) {
							resultsOrdered.push(result[x]);
						}
					}
				}

				return resultsOrdered;
			},
			canEdit() {
				return this.loggedInEmployee.isAdmin && !this.positionTemplate.isArchived;
			},
			duplicateFormTitleErrors() {
				const errors = [];
				if (!this.$v.duplicateForm.title.$dirty) return errors;
				!this.$v.duplicateForm.title.required && errors.push('Position Title is required.');
				return errors;
			}
		},
		methods: {
			printClick() {
				window.print();
			},
			showSnackbar(message, isError) {
				this.snackbar.show = true;
				this.snackbar.message = message;
				this.snackbar.color = isError ? 'error' : undefined;
				this.snackbar.buttonColor = isError ? '#fff' : 'pink';
				this.snackbar.timeout = isError ? 0 : 6000;
			},
			backBtnClicked() {
				window.history.back();
			},
			onEditClick() {
				this.$router.push({ name: 'positionTemplateEdit', params: { id: this.id } });
			},
			onDelete() {
				this.isDeleting = true;
				this.$store.dispatch('positionTemplates/delete', this.positionTemplate).then(
					(response) => {
						this.isDeleting = false;
						this.$router.push({ name: 'positionTemplates', params: { previousAction: 'template_deleted' } });
					},
					(error) => {
						this.isDeleting = false;
						this.showSnackbar('The Position Template could not be deleted at this time');
					}
				);
			},
			onSent() {
				this.showSendEmailDialog = false;
				this.showSnackbar('Position Template sent');
			},
			onDuplicateClick() {
				this.$v.duplicateForm.$reset();
				this.duplicateForm.title = `${this.positionTemplate.title} - copy`;
				this.duplicateDialog = true;
				this.$nextTick(() => this.$refs.duplicateFormTitle.focus());
			},
			duplicatePositionDescription() {
				this.$v.duplicateForm.$touch();

				if (this.$v.duplicateForm.$invalid) {
					console.log('invalid form');
				} else {
					this.isDuplicating = true;

					let positionTemplate = {
						id: this.newGuid(),
						title: this.duplicateForm.title,
						description: this.positionTemplate.description,
						department: this.positionTemplate.department,
						positionItems: this.positionTemplate.positionItems
					};

					this.$store.dispatch('positionTemplates/add', positionTemplate).then(
						(data) => {
							this.isDuplicating = false;
							this.$router.push({ name: 'positionTemplate', params: { id: data.id, previousAction: "position_template_duplicated" }});
						},
						(error) => {
							if (error.response && error.response.status === 409) {
								this.showSnackbar("A position with this name already exists. Please choose a unique name.", true);
							}
							else {
								this.showSnackbar("The Position could not be duplicated at this time", true);
							}
							
							this.isDuplicating = false;
						}
					);
				}
			},
			backBtnClicked() {
				this.$router.push({ name: 'positionTemplates' });
			}
		}
	};
</script>

<style scoped>
	.header {
		min-height: 100px;
		margin-bottom: 25px;
	}
	.header .header-right {
		text-align: right;
	}
	.header h1 {
		font-size: 1.7em;
	}
	.content {
		text-align: left;
	}
	.info-text {
		color: rgba(0, 0, 0, 0.54);
		font-size: 12px;
		font-style: italic;
	}
	.logo img {
		max-height: 100px;
		max-width: 220px;
	}
	.pd-signatures {
		margin-top: -15px;
		text-align: right;
	}
</style>